import { type TagSemanticoDTO } from '@framework/api-client/api/users/schemas/tagSemanticoDTO.ts';
import { ConfigurationCallout } from '@framework/ui/components/configuration-card/ConfigurationCallout.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { IconLink, IconStack2, IconSum } from '@tabler/icons-react';
import { Tag } from 'antd';
import type React from 'react';
import {
  deriveSemanticTagMode,
  SemanticTagMode,
} from '../../../../../semantic-tags/deriveSemanticTagMode.ts';

export interface ISemanticTagModeInfo {
  badge: string;
  badgeColor: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

/** Presentazione (chip e callout) delle modalità del valore di un tag semantico. */
export const SEMANTIC_TAG_MODE_INFO: Record<
  SemanticTagMode,
  ISemanticTagModeInfo
> = {
  [SemanticTagMode.UNIQUE_SHARED]: {
    badge: 'Unico condiviso',
    badgeColor: 'blue',
    icon: <TbIcon icon={IconLink} />,
    title: 'Valore Unico Condiviso',
    description:
      "Un solo valore per l'intera domanda: tutti gli elementi lo condividono e si aggiorna automaticamente in tutti i moduli che usano lo stesso tag.",
  },
  [SemanticTagMode.AGGREGATED]: {
    badge: 'Aggregato',
    badgeColor: 'gold',
    icon: <TbIcon icon={IconSum} />,
    title: 'Valore Aggregato',
    description:
      "Ogni elemento ha il proprio valore, ma nell'elenco risultati vengono combinati in un unico dato calcolato (somma, media, concatenazione…).",
  },
  [SemanticTagMode.MULTIPLE]: {
    badge: 'Multiplo',
    badgeColor: 'geekblue',
    icon: <TbIcon icon={IconStack2} />,
    title: 'Valore Multiplo',
    description:
      "Ogni elemento ha il proprio valore e tutti restano visibili separatamente nell'elenco risultati. Nessuna aggregazione, ogni dato è distinguibile.",
  },
};

export interface ISemanticTagModeCalloutProps {
  semanticTag: TagSemanticoDTO | null | undefined;
}

/**
 * Callout informativo sulla modalità del valore del tag semantico selezionato.
 * Non fa parte del form: non rende nulla se il tag manca o la modalità non è
 * determinabile.
 */
export function SemanticTagModeCallout(props: ISemanticTagModeCalloutProps) {
  const { semanticTag } = props;

  const mode = semanticTag
    ? deriveSemanticTagMode(semanticTag.flMultiplo, semanticTag.aggregazione)
    : undefined;
  if (!mode) return null;

  const info = SEMANTIC_TAG_MODE_INFO[mode];
  return (
    <ConfigurationCallout
      variant="neutral"
      icon={info.icon}
      title={
        <>
          {info.title} <Tag color={info.badgeColor}>{info.badge}</Tag>
        </>
      }
    >
      {info.description}
    </ConfigurationCallout>
  );
}
